import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { HEARTBEAT_MS, LEAD_SECONDS, STAGE_CHANNEL } from './lessonStage';
import { clockNow, initialStageState, lineIndexAt, stageHas, stageModeLabel, stageReducer } from './stageState';
// The full "Color Your Voice" logo (Anthony, 2026-10-30), not the CYV mark.
import logo from '../../../../assets/LOGOcolor.png';
import bird from '../../../../assets/stageBird.svg';
import './StagePage.css';

/**
 * /stage — the room's second screen (Lesson Dashboard pass 3, release 5).
 *
 * A browser window on the room Mac, dragged to the spare monitor once and
 * left there (lessonStage.js has Anthony's words and the whole design). It
 * shows ONLY stage content, driven by the lesson's BroadcastChannel:
 *   Ready   nothing yet
 *   Lyrics  a track's timed words, the current line big at 40% height, 0.3 s
 *           ahead of the player (LEAD_SECONDS), following play, pause and seek
 *   Video   a saved YouTube video, with an "Open on YouTube" fallback
 *   Blank   the logo and tagline, nothing else (black until 2026-10-02)
 * branded (2026-09-28): navy ground, the CYV mark, teal title and line bar;
 * Blank shows the logo. And a small, quiet control row (Lyrics · Video · Blank · Full screen ·
 * Close): always rendered, never hover-gated, muted by colour.
 *
 * It answers the dashboard's ping and says it is here every HEARTBEAT_MS, so
 * the lesson's Second screen control can read "On · Stage window · Lyrics";
 * it says hello on open so a video asked for while it was opening arrives,
 * and a lyrics track already playing re-sends its words; and it says bye on
 * close. Staff-only by route, noindex, no portal chrome.
 *
 * The scroll moves the column with a TRANSFORM, not a smooth scroll: a smooth
 * scroll never completes in a frame-starved tab (CLAUDE.md), and line sizes do
 * not animate, so the target measured when the line changes is exact.
 */
const StagePage = () => {
	const [state, setState] = useState(initialStageState);
	const [closedNote, setClosedNote] = useState(false);
	const channelRef = useRef(null);
	const stateRef = useRef(state);
	stateRef.current = state;

	const dispatch = useCallback((msg) => setState((s) => stageReducer(s, msg, Date.now())), []);
	const post = useCallback((msg) => channelRef.current && channelRef.current.postMessage(msg), []);
	// The playing video's clock goes to the lesson, so its lyrics can follow
	// (pass 4 S4); a lesson's line tap comes back as video-seek.
	const seekRef = useRef(null);
	const videoIdRef = useRef(null);
	videoIdRef.current = state.video ? state.video.videoId : null;
	const postVideoClock = useCallback((c) => post({ type: 'video-clock', videoId: videoIdRef.current, t: c.t, playing: c.playing }), [post]);

	useEffect(() => {
		if (typeof window === 'undefined' || typeof window.BroadcastChannel !== 'function') return undefined;
		const ch = new window.BroadcastChannel(STAGE_CHANNEL);
		channelRef.current = ch;
		ch.onmessage = (e) => {
			const m = (e && e.data) || {};
			if (m.type === 'ping') ch.postMessage({ type: 'stage-here', mode: stateRef.current.mode, has: stageHas(stateRef.current) });
			else if (m.type === 'close') {
				ch.postMessage({ type: 'stage-bye' });
				window.close();
				// A window the browser will not let a script close (opened by hand
				// rather than from the lesson) goes black and says so instead.
				setState((s) => ({ ...s, mode: 'blank' }));
				setClosedNote(true);
			} else if (m.type === 'video-seek') {
				if (seekRef.current && m.videoId === videoIdRef.current) seekRef.current(m.t);
			} else if (['lyrics', 'clock', 'video', 'mode', 'reset'].includes(m.type)) {
				setClosedNote(false);
				setState((s) => stageReducer(s, m, Date.now()));
			}
		};
		ch.postMessage({ type: 'hello', mode: stateRef.current.mode });
		const beat = setInterval(() => ch.postMessage({ type: 'stage-here', mode: stateRef.current.mode, has: stageHas(stateRef.current) }), HEARTBEAT_MS);
		const bye = () => ch.postMessage({ type: 'stage-bye' });
		window.addEventListener('beforeunload', bye);
		return () => {
			clearInterval(beat);
			window.removeEventListener('beforeunload', bye);
			bye();
			ch.close();
			channelRef.current = null;
		};
	}, []);

	// Every mode change (and new words or a new video) is announced at once,
	// not at the next heartbeat, so the lesson's menu is never behind.
	useEffect(() => {
		post({ type: 'stage-here', mode: state.mode, has: stageHas({ lyrics: state.lyrics, video: state.video }) });
		document.title = state.mode === 'video' && state.video ? state.video.title : state.mode === 'lyrics' && state.lyrics ? state.lyrics.title || 'Lyrics' : 'Lesson screen';
	}, [state.mode, state.video, state.lyrics, post]);

	const fullScreen = () => {
		const el = document.documentElement;
		if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen();
		else if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
	};

	return (
		<div className={`stage stage--${state.mode}`} data-mode={state.mode}>
			{state.mode === 'idle' && (
				<div className="stage__idle">
					<img src={logo} alt="Color Your Voice" className="stage__idlelogo" />
					<p className="stage__tagline">Helping young artists find their voice.</p>
					<p>Ready. Play a track with words, or show a video, from the lesson.</p>
				</div>
			)}
			{/* Blank is the logo, not black (Anthony, 2026-10-02: "blank should
			    just be the logo default"): the mark and the tagline on the navy
			    ground, without Ready's line to the teacher. */}
			{state.mode === 'blank' && (
				<div className="stage__idle">
					<img src={logo} alt="Color Your Voice" className="stage__idlelogo" />
					<p className="stage__tagline">Helping young artists find their voice.</p>
					{closedNote && <p>Closed from the lesson. This window cannot close itself; close it here, or leave it for the next lesson.</p>}
				</div>
			)}
			{state.mode === 'video' && state.video && <StageVideo video={state.video} onClock={postVideoClock} seekRef={seekRef} />}
			{state.mode === 'lyrics' && state.lyrics && <StageLyrics lyrics={state.lyrics} clock={state.clock} />}

			<nav className="stage__controls" aria-label="Stage controls">
				<span className="stage__status">{stageModeLabel(state.mode)}</span>
				<button type="button" className={state.mode === 'lyrics' ? 'is-on' : ''} disabled={!state.lyrics} onClick={() => dispatch({ type: 'mode', mode: 'lyrics' })}>
					Lyrics
				</button>
				<button type="button" className={state.mode === 'video' ? 'is-on' : ''} disabled={!state.video} onClick={() => dispatch({ type: 'mode', mode: 'video' })}>
					Video
				</button>
				<button type="button" className={state.mode === 'blank' ? 'is-on' : ''} onClick={() => dispatch({ type: 'mode', mode: 'blank' })}>
					Blank
				</button>
				<button type="button" onClick={fullScreen}>
					Full screen
				</button>
				<button type="button" onClick={() => window.close()}>
					Close
				</button>
			</nav>
		</div>
	);
};

const YT_ORIGIN = 'https://www.youtube.com';

/**
 * The video, and its clock (pass 4 S4, 2026-10-02): the lesson's lyrics
 * follow a lyric video only if the stage says where the video is. The embed
 * speaks YouTube's postMessage protocol (the one its iframe API wraps), so no
 * extra script loads: we ask it to start "listening", it answers with
 * infoDelivery messages carrying currentTime and playerState, and `seekTo`
 * goes back the same way. `onClock({ t, playing })` is called on each update;
 * `seekRef.current(t)` seeks and plays.
 */
export const StageVideo = ({ video, onClock = null, seekRef = null }) => {
	const frame = useRef(null);
	const send = useCallback((msg) => {
		const w = frame.current && frame.current.contentWindow;
		if (w) w.postMessage(JSON.stringify(msg), YT_ORIGIN);
	}, []);

	useEffect(() => {
		if (seekRef) {
			seekRef.current = (t) => {
				send({ event: 'command', func: 'seekTo', args: [Math.max(0, Number(t) || 0), true] });
				send({ event: 'command', func: 'playVideo', args: [] });
			};
		}
		let playing = false;
		const onMessage = (e) => {
			if (e.origin !== YT_ORIGIN || !frame.current || e.source !== frame.current.contentWindow) return;
			let data = e.data;
			try {
				if (typeof data === 'string') data = JSON.parse(data);
			} catch {
				return;
			}
			const info = data && data.info;
			if (!info || typeof info !== 'object') return;
			// playerState: 1 playing, 2 paused, 0 ended, 3 buffering.
			if (typeof info.playerState === 'number') playing = info.playerState === 1;
			if (typeof info.currentTime === 'number' && onClock) onClock({ t: info.currentTime, playing });
		};
		window.addEventListener('message', onMessage);
		return () => {
			window.removeEventListener('message', onMessage);
			if (seekRef) seekRef.current = null;
		};
	}, [video.videoId, onClock, seekRef, send]);

	const origin = typeof window !== 'undefined' ? window.location.origin : '';
	return (
	<div className="stage__video">
		<iframe
			ref={frame}
			title={video.title || 'Video'}
			src={`https://www.youtube.com/embed/${video.videoId}?autoplay=1&rel=0&modestbranding=1&enablejsapi=1&origin=${encodeURIComponent(origin)}`}
			allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
			allowFullScreen
			onLoad={() => send({ event: 'listening', id: video.videoId, channel: 'widget' })}
		/>
		<p className="stage__fallback">
			Video not loading?{' '}
			<a href={`https://www.youtube.com/watch?v=${video.videoId}`} target="_blank" rel="noopener noreferrer">
				Open on YouTube
			</a>
		</p>
	</div>
	);
};

/** The words, the current line at 40% of the screen, following the clock. */
export const StageLyrics = ({ lyrics, clock, now = null }) => {
	const [cur, setCur] = useState(() => lineIndexAt(lyrics.lines, clockNow(clock, now || Date.now(), LEAD_SECONDS)));
	const scrollRef = useRef(null);
	const linesRef = useRef(null);
	const [offset, setOffset] = useState(0);

	// The clock runs on between messages: re-read it every frame while playing.
	useEffect(() => {
		let raf = 0;
		const tick = () => {
			setCur(lineIndexAt(lyrics.lines, clockNow(clock, now || Date.now(), LEAD_SECONDS)));
			if (clock && clock.playing && typeof requestAnimationFrame === 'function') raf = requestAnimationFrame(tick);
		};
		tick();
		return () => raf && typeof cancelAnimationFrame === 'function' && cancelAnimationFrame(raf);
	}, [lyrics, clock, now]);

	// Move the column so the current line (or line 1, before it) sits 40% down.
	useLayoutEffect(() => {
		const list = linesRef.current;
		const box = scrollRef.current;
		if (!list || !box) return;
		const target = list.children[Math.max(0, cur)];
		if (target) setOffset(Math.round(box.clientHeight * 0.4 - target.offsetTop));
	}, [cur, lyrics]);

	return (
		<div className="stage__lyrics">
			{/* The bird behind the rolling words (Anthony, 2026-10-22, from the
			    wallpaper set: "4 bird is awesome"). Decorative, so no alt. */}
			<div className="stage__bird" style={{ '--stage-bird': `url(${bird})` }} aria-hidden="true" />
			<img src={logo} alt="" className="stage__logo" />
			<div className="stage__ltitle">{lyrics.title}</div>
			<div className="stage__lscroll" ref={scrollRef}>
				<div className="stage__llines" ref={linesRef} style={{ transform: `translateY(${offset}px)` }} data-current={cur}>
					{lyrics.lines.map((l, i) => (
						<div key={i} className={`stage__line${i === cur ? ' is-now' : i === cur + 1 || i === cur + 2 ? ' is-next' : ''}`}>
							{l.text}
						</div>
					))}
				</div>
			</div>
			{lyrics.unverified && <div className="stage__lnote">Words heard by ear, unverified</div>}
		</div>
	);
};

export default StagePage;
